import React, { useId, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '../../utils/calculationUtils';

/*
  TrendSparkline: the entity card's ~74px axis-less trend chart.
  - Smoothed (monotone) line in the entity's own color with a soft area fill
    and an emphasized endpoint dot.
  - Optional stepped budget guide: a dashed grey stepAfter line at the budget
    that applied to each month (no text label — the tooltip carries figures).
  - Optional dashed forecast continuation ending in a hollow dot; forecast
    tooltips read "Fcast:" so real history is never confusable with a guess.

  Props:
   - points: [{ month, value }] — actual history, oldest first
   - forecast: [{ month, value }] | null — months after the last actual point
   - budgets: number[] | null — aligned with `points`
   - color: line color (the entity's own color)
   - valueLabel: tooltip label for the value ('Spent' | 'Received' | 'Net' | 'Balance')
   - height: chart height (default 74)
*/

// The app's default category color (#000000) is illegible as a chart line.
const safeColor = (color) =>
  !color || color.toLowerCase() === '#000000' ? '#607d8b' : color;

const SparklineTooltip = ({ active, payload, label, valueLabel }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  const value = point.isForecast ? point.forecast : point.value;
  return (
    <Box
      sx={{
        bgcolor: 'grey.900',
        color: '#fff',
        borderRadius: 1.5,
        px: 1.25,
        py: 0.75,
        boxShadow: 3,
        pointerEvents: 'none',
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, lineHeight: 1.4 }}>
        {point.isForecast ? 'Fcast: ' : ''}
        {label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.4 }}>
        {valueLabel}: {formatCurrency(value)}
      </Typography>
      {typeof point.budget === 'number' && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, lineHeight: 1.4 }}>
          Budget: {formatCurrency(point.budget)}
        </Typography>
      )}
    </Box>
  );
};

const TrendSparkline = ({ points, forecast = null, budgets = null, color, valueLabel = 'Spent', height = 74 }) => {
  const gradientId = useId().replace(/:/g, '');
  const lineColor = safeColor(color);

  const { data, lastActualIndex } = useMemo(() => {
    const actual = (points || []).map((p, i) => ({
      month: p.month,
      value: p.value,
      budget: budgets ? budgets[i] : undefined,
      isForecast: false,
    }));
    const last = actual.length - 1;
    if (last >= 0 && forecast?.length) {
      // Bridge point: the forecast line starts where the actual line ends.
      actual[last] = { ...actual[last], forecast: actual[last].value };
      forecast.forEach((p) => {
        actual.push({ month: p.month, forecast: p.value, isForecast: true });
      });
    }
    return { data: actual, lastActualIndex: last };
  }, [points, forecast, budgets]);

  if (!data.length) return null;
  const lastIndex = data.length - 1;

  // Emphasized endpoints: a filled dot on the last real month, a hollow one
  // at the end of the forecast. All other dots are suppressed.
  const actualDot = (props) => {
    const { key, cx, cy, index } = props;
    if (index !== lastActualIndex || cx == null) return <g key={key} />;
    return <circle key={key} cx={cx} cy={cy} r={3.5} fill={lineColor} stroke="#fff" strokeWidth={1.5} />;
  };
  const forecastDot = (props) => {
    const { key, cx, cy, index } = props;
    if (index !== lastIndex || cx == null) return <g key={key} />;
    return <circle key={key} cx={cx} cy={cy} r={3.5} fill="#fff" stroke={lineColor} strokeWidth={1.5} />;
  };

  return (
    <Box sx={{ height, mx: -0.5 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 6, right: 6, bottom: 2, left: 6 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity={0.18} />
              <stop offset="100%" stopColor={lineColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="month" hide />
          <YAxis hide domain={['auto', 'auto']} />
          <RechartsTooltip
            content={<SparklineTooltip valueLabel={valueLabel} />}
            cursor={{ stroke: '#9aa1ac', strokeDasharray: '2 3' }}
          />
          {budgets && (
            <Line
              dataKey="budget"
              type="stepAfter"
              stroke="#9aa1ac"
              strokeWidth={1}
              strokeDasharray="3 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
          <Area
            dataKey="value"
            type="monotone"
            stroke="none"
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
          <Line
            dataKey="value"
            type="monotone"
            stroke={lineColor}
            strokeWidth={2}
            strokeLinecap="round"
            dot={actualDot}
            activeDot={{ r: 4, strokeWidth: 2 }}
            isAnimationActive={false}
          />
          {forecast?.length > 0 && (
            <Line
              dataKey="forecast"
              type="monotone"
              stroke={lineColor}
              strokeWidth={2}
              strokeDasharray="4 4"
              strokeLinecap="round"
              strokeOpacity={0.85}
              dot={forecastDot}
              activeDot={{ r: 4, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TrendSparkline;
